import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { CHAPTERS } from "../data/chapters";
import { useProgress } from "../context/ProgressContext";
import { useSettings } from "../context/SettingsContext";
import { ACADEMY_LOGO_H } from "../data/assets";
import { Check, CalendarDays, Shield } from "lucide-react";

export default function Sidebar({ onNavigate }) {
  const { completed } = useProgress();
  const { bookingLink } = useSettings();
  const navigate = useNavigate();
  const pct = Math.round((completed.length / CHAPTERS.length) * 100);

  return (
    <div className="flex flex-col h-full bg-[#141414]">
      <div className="p-6 border-b border-white/10">
        <button data-testid="sidebar-logo" onClick={() => { navigate("/"); onNavigate && onNavigate(); }} className="flex items-center gap-3 w-full text-left focus-visible:outline-none">
          <img src={ACADEMY_LOGO_H} alt="The Next O Technical Academy" className="w-40 h-auto object-contain" />
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#C41E24] font-bold mt-1">Training System</div>
          </div>
        </button>
      </div>

      <div className="px-6 py-4 border-b border-white/10">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-white/50">Your Progress</span>
          <span className="text-xs font-bold text-[#C41E24]" data-testid="progress-percent">{pct}%</span>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full bg-[#C41E24] transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-[10px] text-white/40 mt-2">{completed.length} of {CHAPTERS.length} chapters complete</div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3" aria-label="Chapters">
        {CHAPTERS.map((c, i) => {
          const done = completed.includes(c.id);
          const numLabel = String(i + 1).padStart(2, "0");
          return (
            <NavLink
              key={c.id}
              to={`/read/${c.id}`}
              data-testid={`sidebar-nav-${c.id}`}
              onClick={() => onNavigate && onNavigate()}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 border-l-2 transition-colors ${isActive ? "border-[#C41E24] bg-[#C41E24]/10" : "border-transparent hover:bg-white/5"}`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`flex-shrink-0 w-6 h-6 rounded-sm flex items-center justify-center text-[11px] font-bold ${done ? "bg-[#C41E24] text-white" : "bg-white/5 text-white/50"}`}>
                    {done ? <Check size={13} /> : numLabel}
                  </span>
                  <span className={`text-sm font-medium leading-tight ${isActive ? "text-white" : "text-white/65"}`}>{c.title}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-4 border-t border-white/10 space-y-2">
        <a data-testid="sidebar-book-btn" href={bookingLink} target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm transition-colors">
          <CalendarDays size={14} /> Book a Session
        </a>
        <button data-testid="sidebar-coach-btn" onClick={() => { navigate("/coach"); onNavigate && onNavigate(); }}
          className="w-full flex items-center justify-center gap-2 text-[11px] font-bold uppercase tracking-wider text-white/40 hover:text-white transition-colors py-1">
          <Shield size={12} /> Coach Mode
        </button>
      </div>
    </div>
  );
}
